import React from "react";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "—";
  }
}

function InfoItem({ label, value }) {
  const hasValue = value != null && value !== "" && value !== "—";
  return (
    <div className="viewlead-info-item">
      <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
      <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
    </div>
  );
}

export default function TestCaseDetailsPanel({ testCase }) {
  if (!testCase) {
    return (
      <div className="viewlead-card">
        <div className="viewlead-section-title">Test Case</div>
        <div className="viewlead-info-value muted">Test case not found.</div>
      </div>
    );
  }

  const assignedUsers = Array.isArray(testCase.Assigned_Users)
    ? testCase.Assigned_Users
    : Array.isArray(testCase.assigned_users)
      ? testCase.assigned_users
      : [];

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "stretch",
        flexWrap: "wrap",
        marginBottom: 20
      }}
    >
      <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
        <h3 className="viewlead-section-title">Test Case Information</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Test Case ID" value={testCase.Test_Case_Id != null ? String(testCase.Test_Case_Id) : "—"} />
          <InfoItem label="Title" value={testCase.Title || "—"} />
          <InfoItem label="Status" value={testCase.Status || "—"} />
          <InfoItem label="Priority" value={testCase.Priority || "—"} />
          <InfoItem label="Task" value={testCase.Task_Title || testCase.Task_Id || "—"} />
          <InfoItem label="Sub task" value={testCase.Sub_Task_Title || testCase.Sub_Task_Id || "—"} />
        </div>

        {testCase.Description ? (
          <>
            <h3 className="viewlead-section-title">Description</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{testCase.Description}</span>
              </div>
            </div>
          </>
        ) : null}

        {testCase.Pre_Conditions ? (
          <>
            <h3 className="viewlead-section-title">Pre conditions</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{testCase.Pre_Conditions}</span>
              </div>
            </div>
          </>
        ) : null}

        {testCase.Steps ? (
          <>
            <h3 className="viewlead-section-title">Steps</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{testCase.Steps}</span>
              </div>
            </div>
          </>
        ) : null}

        {testCase.Expected_Result ? (
          <>
            <h3 className="viewlead-section-title">Expected result</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{testCase.Expected_Result}</span>
              </div>
            </div>
          </>
        ) : null}

        {testCase.Actual_Result ? (
          <>
            <h3 className="viewlead-section-title">Actual result</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{testCase.Actual_Result}</span>
              </div>
            </div>
          </>
        ) : null}

        {testCase.Comments ? (
          <>
            <h3 className="viewlead-section-title">Comments</h3>
            <div className="viewlead-info-grid">
              <div className="viewlead-info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="viewlead-info-value">{testCase.Comments}</span>
              </div>
            </div>
          </>
        ) : null}
      </div>

      <div className="viewlead-card" style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}>
        <h3 className="viewlead-section-title">Created</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          <InfoItem label="Created By" value={testCase.Created_By_Name || testCase.Created_By || "—"} />
          <InfoItem label="Created On" value={formatDateTime(testCase.Created_On)} />
        </div>

        <h3 className="viewlead-section-title">Executed</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          <InfoItem label="Executed By" value={testCase.Executed_By_Name ?? testCase.Executed_By ?? "—"} />
          <InfoItem label="Executed On" value={formatDateTime(testCase.Executed_On)} />
        </div>

        <h3 className="viewlead-section-title">Assigned Team Members</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {assignedUsers.length > 0 ? (
            assignedUsers.map((u, idx) => {
              const fullName = `${u.First_Name ?? u.first_name ?? ""} ${u.Last_Name ?? u.last_name ?? ""}`.trim();
              const name = u.User_Name ?? u.user_name ?? u.name ?? (fullName || "—");
              return (
                <div key={u.User_Id ?? u.user_id ?? idx} className="viewlead-info-item">
                  <span className="viewlead-info-value">{name}</span>
                </div>
              );
            })
          ) : (
            <div className="viewlead-info-item">
              <span className="viewlead-info-value muted">No team members assigned</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
